import React, { MutableRefObject, useEffect, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { CompanyUserType } from 'lib/api_ts/companiesApi';
import { RawTaskType } from '../Gantt';
import { TaskStatusMapType } from '../Overview';
import { DeliverableType, MilestoneType, StreamType, SubtaskType, TaskType } from 'lib/api_ts/overviewApi';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';

interface DetailPopoverProps {
	possibleResponsibles: CompanyUserType[];
	taskStatusMap: TaskStatusMapType;
	activeKey: string;

	// N.D.R.
	// svarr react Gantt doesn't expose types for ganttApiRef,
	// so we use unknown here
	ganttApiRef: MutableRefObject<unknown>;
	gridRef: MutableRefObject<AgGridReact>;
	allRawTasks: {
		wp: StreamType[];
		task: TaskType[];
		subtask: SubtaskType[];
		milestone: MilestoneType[];
		deliverable: DeliverableType[];
	};
	selectedZoomLevel: string;
	ganttContainerRef: MutableRefObject<HTMLDivElement>;
}

const DetailPopover: React.FC<DetailPopoverProps> = ({
	possibleResponsibles,
	taskStatusMap,
	activeKey,
	ganttApiRef,
	gridRef,
	allRawTasks,
	selectedZoomLevel,
	ganttContainerRef,
}) => {
	const [selectedTask, setSelectedTask] = useState<RawTaskType>();
	const [selectedTaskElement, setSelectedTaskElement] = useState<Element>();

	useEffect(() => {
		if (!ganttApiRef.current) return;

		const handleSelectTask = (task: { id: string }) => {
			const [taskType, extractedId] = task.id.split('-');
			const taskElement = document.querySelector(`[data-id="${taskType}-${extractedId}"].wx-bar`);
			if (!taskElement) return;
			setSelectedTaskElement(taskElement);
			setSelectedTask({
				...allRawTasks[taskType].find((singleRawTask: { id: number }) => singleRawTask.id === parseInt(task.id.split('-')[1])),
				taskType,
			});
		};

		// N.D.R. type guard to safely handle ganttApiRef.current = unknown
		if (
			ganttApiRef.current &&
			typeof ganttApiRef.current === 'object' &&
			'on' in ganttApiRef.current &&
			typeof ganttApiRef.current.on === 'function'
		) {
			ganttApiRef.current.on('select-task', handleSelectTask);
		}
	}, [allRawTasks, ganttApiRef]);

	useEffect(() => {
		if (!ganttContainerRef.current) return;
		const scrollingElement = ganttContainerRef.current.querySelector('.wx-chart.x2-1ff484e') as HTMLElement;

		if (!scrollingElement || !selectedTaskElement) return;
		const handleScroll = () => {
			const rect = selectedTaskElement.getBoundingClientRect();
			const containerRect = ganttContainerRef.current.getBoundingClientRect();
			const scrollLeft = rect.left - containerRect.left + scrollingElement.scrollLeft - containerRect.width / 2 + rect.width / 2;

			scrollingElement.scrollTo({
				left: scrollLeft,
			});
		};

		const timer = setTimeout(handleScroll, 1);
		return () => clearTimeout(timer);
	}, [selectedTaskElement, ganttContainerRef]);

	useEffect(() => {
		if (activeKey === 'overview') return;
		setSelectedTask(undefined);
		setSelectedTaskElement(undefined);
	}, [activeKey]);

	useEffect(() => {
		if (!selectedTask || !selectedTaskElement) return;

		const handleClickOutside = (event: MouseEvent) => {
			const popoverElement = document.querySelector(`#detail_popover-${selectedTask.taskType}-${selectedTask.id}`);
			const cellLabelElement = document.querySelector(`#cellLabel-${selectedTask.taskType}-${selectedTask.id}`);

			if (!popoverElement?.contains(event.target as Node) && !cellLabelElement?.contains(event.target as Node)) {
				setSelectedTask(undefined);
				setSelectedTaskElement(undefined);
				gridRef.current?.api?.deselectAll();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [selectedTask, selectedTaskElement, gridRef]);

	if (!selectedTask || !selectedTaskElement) {
		return null;
	}

	const title = selectedTask ? ('name' in selectedTask ? selectedTask.name : 'title' in selectedTask ? selectedTask.title : '') : '';

	const startDate = selectedTask
		? 'forecastDate' in selectedTask && typeof selectedTask.forecastDate === 'string'
			? format(selectedTask.forecastDate, 'd/MM/yyyy', { locale: it })
			: 'startDate' in selectedTask
				? format(selectedTask.startDate, 'd/MM/yyyy', { locale: it })
				: ''
		: '';

	const endDate = selectedTask
		? 'endDate' in selectedTask && selectedTask.endDate !== null
			? format(selectedTask.endDate, 'd/MM/yyyy', { locale: it })
			: 'effectiveDate' in selectedTask && selectedTask.effectiveDate !== null
				? format(selectedTask.effectiveDate, 'd/MM/yyyy', { locale: it })
				: ''
		: '';
	const responsibleUser = possibleResponsibles.find((possibleResponsible) =>
		selectedTask
			? ('responsible' in selectedTask && possibleResponsible.id === selectedTask.responsible) ||
				('owner' in selectedTask && possibleResponsible.email === selectedTask.owner)
			: false,
	);

	const responsible = selectedTask
		? responsibleUser
			? `${responsibleUser.firstName} ${responsibleUser.lastName}`
			: 'responsibles' in selectedTask && selectedTask.responsibles.length > 0
				? selectedTask.responsibles[0].name
				: ''
		: '';

	const hasRangeDates =
		selectedTask.taskType !== 'subtask' && selectedTask.taskType !== 'milestone' && selectedTask.taskType !== 'deliverable';

	const status = !hasRangeDates && taskStatusMap.find((status) => status.code === selectedTask.status);

	const startMonth =
		'monthsStartDate' in selectedTask
			? selectedTask.monthsStartDate
			: 'monthsForecastDate' in selectedTask
				? selectedTask.monthsForecastDate
				: '';
	const endMonth =
		'monthsEndDate' in selectedTask
			? selectedTask.monthsEndDate
			: 'monthsEffectiveDate' in selectedTask
				? selectedTask.monthsEffectiveDate
				: '';

	return (
		<Overlay
			show={!!selectedTask}
			target={selectedTaskElement}
			placement="bottom"
			popperConfig={{
				modifiers: [
					{
						name: 'flip',
						enabled: true,
						options: {
							fallbackPlacements: ['top'],
							padding: 10,
						},
					},
				],
			}}
			container={ganttContainerRef.current}
		>
			<Popover
				id={`detail_popover-${selectedTask.taskType}-${selectedTask.id}`}
				style={{
					minWidth: '300px',
					// fontFamily: 'Barlow, sans-serif',
					// fontFamily: 'Atkinson Hyperlegible Next, sans-serif',
					fontFamily: 'var(--font-primary)',
					zIndex: 10,
				}}
				className={selectedZoomLevel === 'daily' ? 'popover-daily' : ''}
			>
				<Popover.Header
					className="d-flex justify-content-between p-3 border-0"
					style={{
						color: '#1C2743',
					}}
				>
					<div
						className="fw-bold"
						style={{
							fontSize: '18px',
							lineHeight: '30px',
						}}
					>
						{title}
					</div>

					<button
						type="button"
						className="btn-close"
						aria-label="Close"
						style={{
							boxSizing: 'border-box',
							width: '16px',
							height: '16px',
							backgroundSize: '8px 8px',
							opacity: '1',
						}}
						onClick={() => {
							setSelectedTask(undefined);
							setSelectedTaskElement(undefined);
							gridRef.current?.api?.deselectAll();
						}}
					/>
				</Popover.Header>
				<Popover.Body
					style={{
						fontSize: '16px',
						lineHeight: '25px',
						color: '#2C3D68',
					}}
				>
					<div>
						<span className="fw-bold">{endDate && hasRangeDates ? 'Inizio attività' : 'Data prevista'}:</span> {startDate}
					</div>
					<div>
						<span className="fw-bold">{endMonth && hasRangeDates ? 'Mese Inizio attività' : 'Mese previsto'}:</span>{' '}
						{startMonth ? `M${startMonth}` : 'N.D.'}
					</div>
					<div>
						<span className="fw-bold">{hasRangeDates ? 'Fine attività' : 'Data effettiva'}:</span> {endDate || 'N.D.'}
					</div>
					<div>
						<span className="fw-bold">{hasRangeDates ? 'Mese Fine attività' : 'Data effettiva'}:</span> {endMonth ? `M${endMonth}` : 'N.D.'}
					</div>

					{'objective' in selectedTask && selectedTask.objective !== null ? (
						<div>
							<span className="fw-bold">{!hasRangeDates ? 'Descrizione' : 'Obiettivo'}:</span> {selectedTask.objective}
						</div>
					) : null}
					{responsible ? (
						<div>
							<span className="fw-bold">{!hasRangeDates ? 'Ownership' : 'Responsabile'}:</span> {responsible}
						</div>
					) : null}
					{status ? (
						<div>
							<span className="fw-bold">Status: </span>
							{status.name}
						</div>
					) : null}
				</Popover.Body>
			</Popover>
		</Overlay>
	);
};

export default DetailPopover;
